/* !
 * index.js v1.0.0
 * Copyright, Connecty Inc.
 */
((FUNCTIONS) => {
	const a = 'is-active';
	const v = 'is-visible';
	const $path = location.pathname;
	const thisSearch = location.search;
	let thisParam = [];
	if(thisSearch) { thisParam = thisSearch.replace(/\?/, '').split('&'); }
	let thisLat, thisLon, thisMax, thisZoom, thisCity, thisKeyword;
	let currentPin = '/images/jihanki/map/location.webp';
	let currentMap = null; // 現在のマップインスタンスを保持
	let lastSearchCenter = null; // 最後に検索した中心位置を保持
	let suppressNextIdleSearch = false; // 明示検索の直後、fitBounds由来のidle自動再検索を1回抑止
	let searchMode = null; // 検索モード 'current'（現在地） | 'address'（住所）
	// 種類フィルタ選択時に、該当自販機がある都道府県／市区町村（無い選択肢はdisableする）
	let matchedPrefectureSet = null;   // null=フィルタなし（全選択可）
	let matchedCityMap = null;         // { 都道府県: Set(市区町村) }
	// 全国検索時の初期表示（日本全体）
	const JAPAN_CENTER = { lat: 36.2048, lng: 138.2529 };
	// 全国検索のサンプリング設定（総件数が多く全件描画は不可のため、全国から分散して抽出）
	const NATIONWIDE_TOTAL = 125499;   // 概算総件数（分散サンプリングのストライド算出用）
	const NATIONWIDE_BUCKETS = 12;     // 分割数（IDは地域順のため、分割抽出で全国に分散させる）
	const NATIONWIDE_PER_BUCKET = 100; // 1分割あたりの取得件数（最大 BUCKETS×PER_BUCKET 件を表示）

	// 種類フラグ → 表示ラベル（ポップアップの「自販機」に使用）
	// チェックボックスの name／URLパラメータのキーもこのフィールド名（vending_machine_typeN）
	const MACHINE_TYPE_LABEL = {
		vending_machine_type1: 'お菓子付き自販機',
		vending_machine_type2: '子育て支援自販機',
		vending_machine_type3: '女性活躍応援自販機',
	};
	// 検索クエリで取得する／絞り込みに使う種類フラグのフィールド一覧
	const MACHINE_TYPE_FIELDS = Object.keys(MACHINE_TYPE_LABEL);
	// 商品名フィールド（検索関数は product_name1〜product_name10 を返す）
	const PRODUCT_NAME_FIELDS = Array.from({ length: 10 }, (_, i) => `product_name${i + 1}`);
	// 種類フラグは検索関数では 0/1、テーブルでは true/false で返るため両対応
	const isTypeFlagOn = (v) => v === true || v === 1 || v === '1';
	// 値が「空でも0でもない」＝実データが入っているか
	const hasMeaningfulValue = (v) => v !== undefined && v !== null && v !== false && v !== 0 && ('' + v).trim() !== '' && ('' + v).trim() !== '0';

	// ===== 商品絞り込みの選択肢 ======================================
	// HTMLの .js-filter-product から取得（value=商品ID、data-product-name または表示テキスト=商品名）。商品IDの昇順で返す
	const getProductFilterOptions = () => {
		return Array.from(document.querySelectorAll('.js-filter-product'))
			.map(checkbox => {
				const label = checkbox.closest('.jihanki-map-search-product');
				const text = label && label.querySelector('.jihanki-map-search-product-name');
				const name = (checkbox.dataset.productName || (text ? text.textContent : '')).trim();
				return { id: checkbox.value, name, checkbox };
			})
			.filter(option => option.id && option.name)
			.sort((a, b) => Number(a.id) - Number(b.id));
	};
	// 商品絞り込みの選択肢を商品IDの昇順に並べ替え（後日商品が追加されてもHTMLの記述順に依存しない）
	const sortProductFilterOptions = () => {
		const container = document.querySelector('.jihanki-map-search-filter-products');
		if (!container) return;
		getProductFilterOptions().forEach(option => {
			const label = option.checkbox.closest('.jihanki-map-search-product');
			if (label && label.parentNode === container) container.appendChild(label);
		});
	};

	// GraphQL API ===============================================
	// GraphQLクエリを実行する汎用関数
	const fetchGraphQL = async (query, variables = {}) => {
		const response = await fetch('https://vending-api.dydo.cmsod.jp/v1/graphql', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ query, variables })
		});
		
		const { data, errors } = await response.json();
		if (errors) {
			console.error('GraphQL errors:', errors);
			throw new Error(errors[0].message);
		}
		return data;
	};

	// 地理検索（現在地から検索）
	const searchVendingMachinesByLocation = async (longitude, latitude, radius) => {
		const query = `
			query SearchVendingMachines($lon: numeric!, $lat: numeric!, $radius: Int!) {
				search_vending_machines(args: {
					p_longitude: $lon
					p_latitude: $lat
					p_max: $radius
				}) {
					vending_machine_id
					longitude
					latitude
					prefecture
					city
					address
					base
					inner
					${MACHINE_TYPE_FIELDS.join('\n\t\t\t\t\t')}
					${PRODUCT_NAME_FIELDS.join('\n\t\t\t\t\t')}
				}
			}
		`;
		return fetchGraphQL(query, { lon: longitude, lat: latitude, radius });
	};

	// 都道府県で検索
	const searchVendingMachinesByPrefecture = async (prefecture) => {
		// console.log('Searching by prefecture:', prefecture);
		const query = `
			query GetByPrefecture($prefecture: String!) {
				vending_machines(
					where: { prefecture: { _eq: $prefecture } }
					order_by: { city: asc }
				) {
					vending_machine_id
					longitude
					latitude
					prefecture
					city
					address
					base
					inner
					${MACHINE_TYPE_FIELDS.join('\n\t\t\t\t\t')}
				}
			}
		`;
		const result = await fetchGraphQL(query, { prefecture });
		// console.log('Prefecture search result:', result);
		return result;
	};

	// 都道府県と市区町村で検索
	const searchVendingMachinesByPrefectureAndCity = async (prefecture, city, limit = 1000) => {

		const query = `
			query GetByPrefectureAndCity($prefecture: String!, $city: String!, $limit: Int!) {
				vending_machines(
					where: { 
						_and: [
							{ prefecture: { _eq: $prefecture } }
							{ city: { _eq: $city } }
						]
					}
					limit: $limit
					order_by: { vending_machine_id: asc }
				) {
					vending_machine_id
					longitude
					latitude
					prefecture
					city
					address
					base
					inner
					${MACHINE_TYPE_FIELDS.join('\n\t\t\t\t\t')}
				}
			}
		`;
		return fetchGraphQL(query, { prefecture, city, limit });
	};

	// 自販機一覧取得（制限付き）
	const getVendingMachines = async (limit = 1000, offset = 0) => {
		const query = `
			query GetVendingMachines($limit: Int!, $offset: Int) {
				vending_machines(
					limit: $limit
					offset: $offset
					order_by: { vending_machine_id: asc }
				) {
					vending_machine_id
					longitude
					latitude
					prefecture
					city
					address
					base
					inner
					${MACHINE_TYPE_FIELDS.join('\n\t\t\t\t\t')}
				}
			}
		`;
		return fetchGraphQL(query, { limit, offset });
	}

	// 全国検索用：全国から分散したサンプルを取得（全件は多すぎて描画不可のため）
	const getNationwideSample = async () => {
		const stride = Math.max(1, Math.floor(NATIONWIDE_TOTAL / NATIONWIDE_BUCKETS));
		const requests = [];
		for (let i = 0; i < NATIONWIDE_BUCKETS; i++) {
			requests.push(
				getVendingMachines(NATIONWIDE_PER_BUCKET, i * stride).catch(err => {
					console.error('全国サンプル取得に失敗しました:', err);
					return { vending_machines: [] };
				})
			);
		}
		const results = await Promise.all(requests);
		return results.flatMap(r => (r && r.vending_machines) ? r.vending_machines : []);
	};

	// 自販機の種類フラグで直接検索（全国から該当する自販機のみ取得。複数指定はOR）
	const searchVendingMachinesByTypes = async (typeFields, limit = 10000) => {
		const orConditions = typeFields.map(field => `{ ${field}: { _eq: true } }`).join(' ');
		const query = `
			query GetByTypes($limit: Int!) {
				vending_machines(
					where: { _or: [ ${orConditions} ] }
					limit: $limit
					order_by: { vending_machine_id: asc }
				) {
					vending_machine_id
					longitude
					latitude
					prefecture
					city
					address
					base
					inner
					${MACHINE_TYPE_FIELDS.join('\n\t\t\t\t\t')}
				}
			}
		`;
		const data = await fetchGraphQL(query, { limit });
		return data.vending_machines || [];
	};

	// 表示範囲(bounds)内の、種類フラグに該当する自販機を取得（種類フィルタ時の地図移動用）
	const searchVendingMachinesByBoundsAndTypes = async (sw, ne, typeFields, limit = 2000) => {
		const orConditions = typeFields.map(field => `{ ${field}: { _eq: true } }`).join(' ');
		const query = `
			query GetByBoundsAndTypes($swLat: numeric!, $neLat: numeric!, $swLng: numeric!, $neLng: numeric!, $limit: Int!) {
				vending_machines(
					where: {
						_and: [
							{ latitude: { _gte: $swLat, _lte: $neLat } }
							{ longitude: { _gte: $swLng, _lte: $neLng } }
							{ _or: [ ${orConditions} ] }
						]
					}
					limit: $limit
				) {
					vending_machine_id
					longitude
					latitude
					prefecture
					city
					address
					base
					inner
					${MACHINE_TYPE_FIELDS.join('\n\t\t\t\t\t')}
				}
			}
		`;
		const data = await fetchGraphQL(query, {
			swLat: sw.lat(), neLat: ne.lat(), swLng: sw.lng(), neLng: ne.lng(), limit
		});
		return data.vending_machines || [];
	};

	// 指定エリア（都道府県／＋市区町村）に存在する種類フラグ（type1〜3）の集合を取得
	const getAvailableTypesForArea = async (prefecture, city) => {
		const varDefs = city ? '$prefecture: String!, $city: String!' : '$prefecture: String!';
		const areaConds = city
			? '{ prefecture: { _eq: $prefecture } } { city: { _eq: $city } }'
			: '{ prefecture: { _eq: $prefecture } }';
		const aliases = MACHINE_TYPE_FIELDS.map((field, i) =>
			`a${i}: vending_machines(where: { _and: [ ${areaConds} { ${field}: { _eq: true } } ] }, limit: 1) { vending_machine_id }`
		).join('\n\t\t\t\t');
		const query = `query GetAvailableTypes(${varDefs}) {\n\t\t\t\t${aliases}\n\t\t\t}`;
		const vars = city ? { prefecture, city } : { prefecture };
		const data = await fetchGraphQL(query, vars);
		const set = new Set();
		MACHINE_TYPE_FIELDS.forEach((field, i) => {
			if ((data['a' + i] || []).length > 0) set.add(field);
		});
		return set;
	};

	// 商品で全国検索：近傍検索関数（search_vending_machines）を日本の中心・半径3,000kmで実行し、
	// 選択した商品を扱う自販機のみ取得する（vending_machines テーブルには商品列が無いため関数側で絞り込む）
	// ※複数商品は p_product_id1〜10 に順に指定（最大10件）
	const NATIONWIDE_RADIUS = 3000000;
	const searchVendingMachinesByProducts = async (productIds) => {
		const ids = productIds.map(id => parseInt(id, 10)).filter(id => !isNaN(id)).slice(0, 10);
		if (!ids.length) return [];
		const productArgs = ids.map((id, i) => `p_product_id${i + 1}: ${id}`).join('\n\t\t\t\t\t');
		const query = `
			query GetByProducts($lon: numeric!, $lat: numeric!, $radius: Int!) {
				search_vending_machines(args: {
					p_longitude: $lon
					p_latitude: $lat
					p_max: $radius
					${productArgs}
				}) {
					vending_machine_id
					longitude
					latitude
					prefecture
					city
					address
					base
					inner
					${MACHINE_TYPE_FIELDS.join('\n\t\t\t\t\t')}
					${PRODUCT_NAME_FIELDS.join('\n\t\t\t\t\t')}
				}
			}
		`;
		// ※全国検索でピン表示にも使うため、位置・住所・種類フラグも取得する
		const data = await fetchGraphQL(query, { lon: JAPAN_CENTER.lng, lat: JAPAN_CENTER.lat, radius: NATIONWIDE_RADIUS });
		return data.search_vending_machines || [];
	};

	// 商品の絞り込みは商品IDで行う（DBの商品名は略称のため、表示用の商品名とは突合できない）
	// 選択した商品IDを扱う自販機IDの集合を API（p_product_id）で取得し、検索結果の自販機IDと照合する
	// ※都道府県・市区町村・全国検索の vending_machines テーブルには商品列が無いため、どの検索経路でもこの集合で判定する
	let selectedProductMachineIds = null; // null=商品未選択（setMarker の開始時に URL の product から取得）
	const productMachinesCache = {};      // 商品IDの組み合わせごとの、扱う自販機一覧（Promise）
	// vending_machine_id には末尾に空白が付いたものがあるため、照合時は前後の空白を除去
	const normalizeMachineId = (id) => ('' + (id ?? '')).trim();
	// 選択商品を扱う自販機の一覧（位置情報付き）を取得（同じ組み合わせは再取得しない）
	// ※全国検索・地図移動時の再検索で、種類の該当自販機と合わせて表示するためにも使う
	const getProductMachines = (productIds) => {
		const key = [...productIds].sort().join(',');
		if (!productMachinesCache[key]) {
			productMachinesCache[key] = searchVendingMachinesByProducts(productIds).catch(error => {
				delete productMachinesCache[key];
				throw error;
			});
		}
		return productMachinesCache[key];
	};
	const loadSelectedProductMachineIds = async () => {
		const productIds = new URLSearchParams(window.location.search).getAll('product').filter(Boolean);
		if (!productIds.length) {
			selectedProductMachineIds = null;
			return;
		}
		try {
			const list = await getProductMachines(productIds);
			selectedProductMachineIds = new Set(list.map(item => normalizeMachineId(item.vending_machine_id)));
		} catch (error) {
			console.error('商品を扱う自販機の取得に失敗しました:', error);
			selectedProductMachineIds = new Set();
		}
	};
	// 2つの自販機一覧を自販機IDで重複なく結合（種類・商品の「どちらかに当てはまる」表示用）
	const mergeMachineLists = (a, b) => {
		const seen = new Set();
		return [...(a || []), ...(b || [])].filter(item => {
			const id = normalizeMachineId(item.vending_machine_id);
			if (seen.has(id)) return false;
			seen.add(id);
			return true;
		});
	};
	// 自販機が選択した商品（商品ID）のいずれかを扱っているか
	const hasSelectedProduct = (item, productIds) => {
		return !!selectedProductMachineIds && selectedProductMachineIds.has(normalizeMachineId(item.vending_machine_id));
	};

	// 選択した商品を扱う自販機がある地域（都道府県・市区町村）のレコード一覧を取得
	const getAreasWithProducts = async (productIds) => {
		return getProductMachines(productIds);
	};
	// 都道府県一覧を取得（ユニーク）
	const getPrefectures = async () => {
		const query = `
			query GetPrefectures {
				vending_machines(distinct_on: prefecture, order_by: { prefecture: asc }) {
					prefecture
				}
			}
		`;
		const data = await fetchGraphQL(query);
		return data.vending_machines.map(item => item.prefecture);
	};

	// 都道府県に紐づく市区町村一覧を取得（ユニーク）
	const getCitiesByPrefecture = async (prefecture) => {
		const query = `
			query GetCitiesByPrefecture($prefecture: String!) {
				vending_machines(
					where: { prefecture: { _eq: $prefecture } }
					distinct_on: city
					order_by: { city: asc }
				) {
					city
				}
			}
		`;
		const data = await fetchGraphQL(query, { prefecture });
		return data.vending_machines
			.map(item => item.city)
			.filter(city => city && city.trim() !== '' && city !== '#VALUE!' && !city.includes('#VALUE')); // null/空/#VALUE!を除外
	};

	// 市区町村コードCSVを読み込む
	let cityCodeMap = null; // {都道府県名: [{code: 標準地域コード, city: 市区町村名}]}
	const loadCityCodeMap = async () => {
		if (cityCodeMap) return cityCodeMap; // キャッシュがあれば返す
		
		try {
			const response = await fetch('/jihanki/map/市区町村コード.csv');
			const csvText = await response.text();
			const lines = csvText.split('\n');
			
			cityCodeMap = {};
			
			// 1行目はヘッダーなのでスキップ
			for (let i = 1; i < lines.length; i++) {
				const line = lines[i].trim();
				if (!line) continue;
				
				const [code, prefecture, city] = line.split(',');
				if (!code || !prefecture || !city) continue;
				
				if (!cityCodeMap[prefecture]) {
					cityCodeMap[prefecture] = [];
				}
				
				cityCodeMap[prefecture].push({
					code: parseInt(code, 10),
					city: city
				});
			}
			
			return cityCodeMap;
		} catch (error) {
			console.error('市区町村コードCSVの読み込みに失敗しました:', error);
			return null;
		}
	};

	// 市区町村リストを標準地域コード順にソート
	const sortCitiesByCode = async (prefecture, cities) => {
		const codeMap = await loadCityCodeMap();
		if (!codeMap || !codeMap[prefecture]) {
			// CSVが読み込めない場合は元の順序を返す
			return cities;
		}
		
		// 市区町村名からコードを検索するマップを作成
		const cityToCode = {};
		codeMap[prefecture].forEach(item => {
			cityToCode[item.city] = item.code;
		});
		
		// コードがある市区町村とない市区町村を分ける
		const citiesWithCode = [];
		const citiesWithoutCode = [];
		
		cities.forEach(city => {
			if (cityToCode[city] !== undefined) {
				citiesWithCode.push({ city, code: cityToCode[city] });
			} else {
				citiesWithoutCode.push(city);
			}
		});
		
		// コードがある市区町村をコード順にソート
		citiesWithCode.sort((a, b) => a.code - b.code);
		
		// コードがある市区町村とない市区町村を結合（コードがないものは最後）
		return [...citiesWithCode.map(item => item.city), ...citiesWithoutCode];
	};


	// ============ ↓↓ FUNCTIONS ↓↓ =＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
	// 中心点から指定した半径(m)の範囲を表示（半径3km = ズームレベル15）
	const setBoundsFromCenter = (map, centerLat, centerLng, radiusInMeters = 3000) => {
		const center = new google.maps.LatLng(centerLat, centerLng);
		map.setCenter(center);
		
		// 半径に応じた適切なズームレベルを設定
		let zoom;
		if (radiusInMeters <= 1000) {
			zoom = 16; // 1km以下
		} else if (radiusInMeters <= 3000) {
			zoom = 15; // 3km以下
		} else if (radiusInMeters <= 5000) {
			zoom = 14; // 5km以下
		} else if (radiusInMeters <= 10000) {
			zoom = 13; // 10km以下
		} else {
			zoom = 12; // それ以上
		}
		
		map.setZoom(zoom);
	};

	// 指定した結果一覧が収まるように地図の表示範囲を合わせる
	const fitMapToResults = (map, list) => {
		const points = (list || []).filter(x => x.latitude && x.longitude);
		if (!points.length) return false;
		const bounds = new google.maps.LatLngBounds();
		points.forEach(x => bounds.extend(new google.maps.LatLng(Number(x.latitude), Number(x.longitude))));
		map.fitBounds(bounds);
		return true;
	};

	const maxToZoom = (_max) => {
		let zoom;
		if(FUNCTIONS.va.window.width > 768) {
			switch(_max) {
			case'1000000': zoom = 5; break;
			case'500000': zoom = 6; break;
			case'200000': zoom = 7; break;
			case'150000': zoom = 8; break;
			case'100000': zoom = 9; break;
			case'50000': zoom = 10; break;
			case'20000': zoom = 11; break;
			case'10000': zoom = 12; break;
			case'5000': zoom = 13; break;
			case'2000': zoom = 14; break;
			case'1000': zoom = 15; break;
			case'500': zoom = 16; break;
			case'200': zoom = 17; break;
			case'100': zoom = 18; break;
			case'50': zoom = 19; break;
			case'20': zoom = 20; break;
			case'10': zoom = 21; break;
			default: zoom = 12; break;
			}
		} else {
			switch(_max) {
			case'1000000': zoom = 4; break;
			case'500000': zoom = 5; break;
			case'200000': zoom = 6; break;
			case'150000': zoom = 7; break;
			case'100000': zoom = 8; break;
			case'50000': zoom = 9; break;
			case'20000': zoom = 10; break;
			case'10000': zoom = 11; break;
			case'5000': zoom = 12; break;
			case'2000': zoom = 13; break;
			case'1000': zoom = 14; break;
			case'500': zoom = 15; break;
			case'200': zoom = 16; break;
			case'100': zoom = 17; break;
			case'50': zoom = 18; break;
			case'20': zoom = 19; break;
			case'10': zoom = 20; break;
			default: zoom = 12; break;
			}
		}
		return zoom;
	};

	// 検索パラメータを変数にセット
	const setParamForMap = () => {
		for(let i=0; i<thisParam.length; i++) {
			switch(thisParam[i].split('=')[0]) {
			case'p_lat': thisLat = Number(thisParam[i].split('=')[1]); break;
			case'p_lon': thisLon = Number(thisParam[i].split('=')[1]); break;
			case'p_max': thisMax = Number(thisParam[i].split('=')[1]); thisZoom = maxToZoom(thisParam[i].split('=')[1]); break;
			default:
			}
		}
		// 全国検索（住所検索で都道府県未選択）：日本全体を初期表示し、以降は地図移動で範囲検索
		const isNationwide = new URLSearchParams(window.location.search).get('area') === 'all';
		if (isNationwide && thisLat === undefined && thisLon === undefined) {
			thisLat = JAPAN_CENTER.lat;
			thisLon = JAPAN_CENTER.lng;
			thisZoom = (FUNCTIONS.va.window.width > 768) ? 5 : 4;
		}

		if(!thisZoom) { thisZoom = 12; }
	};

	// 検索パラメータ引き継ぎ
	const setParam = () => {
		// 緯度経度、範囲意外の絞り込みパラメータとキーワードのみ引き継ぎする
		let newParam = '';
		for(let i=0; i<thisParam.length; i++) {
			if(thisParam[i].split('=')[0] !== 'p_lat' &&
				thisParam[i].split('=')[0] !== 'p_lon' &&
				thisParam[i].split('=')[0] !== 'p_max') {
				newParam += `&${thisParam[i].split('=')[0]}=${thisParam[i].split('=')[1]}`;
			}
		}
		return newParam;
	};

	// レコードから、真の種類フラグに対応するラベル一覧を取得
	const getMachineTypeLabels = (item) => {
		return Object.keys(MACHINE_TYPE_LABEL)
			.filter(field => isTypeFlagOn(item[field]))
			.map(field => MACHINE_TYPE_LABEL[field]);
	};

	// レコードから、空でない商品名一覧を取得
	const getProductNames = (item) => {
		return PRODUCT_NAME_FIELDS
			.map(field => item[field])
			.filter(v => hasMeaningfulValue(v))
			.map(v => ('' + v).trim());
	};

	// 商品名を持たないレコード（都道府県・市区町村・全国検索の vending_machines テーブル）用に、
	// 自販機の座標で近傍検索（search_vending_machines）を行い、同じ自販機IDの商品名一覧を取得する
	const productNamesCache = {}; // 自販機ID → Promise<商品名の配列>
	const fetchProductNamesForMachine = (record) => {
		const id = normalizeMachineId(record.vending_machine_id);
		if (!productNamesCache[id]) {
			const query = `
				query GetMachineProducts($lon: numeric!, $lat: numeric!, $radius: Int!) {
					search_vending_machines(args: {
						p_longitude: $lon
						p_latitude: $lat
						p_max: $radius
					}) {
						vending_machine_id
						${PRODUCT_NAME_FIELDS.join('\n\t\t\t\t\t\t')}
					}
				}
			`;
			productNamesCache[id] = fetchGraphQL(query, { lon: Number(record.longitude), lat: Number(record.latitude), radius: 10 })
				.then(data => {
					const machine = (data.search_vending_machines || []).find(item => normalizeMachineId(item.vending_machine_id) === id);
					return machine ? getProductNames(machine) : [];
				})
				.catch(error => {
					delete productNamesCache[id];
					throw error;
				});
		}
		return productNamesCache[id];
	};
	// 商品名を持たないレコード一覧に、商品名（product_name1〜10）をまとめて付与する（ピンを立てる前に実行）
	// 表示範囲を覆う円で近傍検索を1回だけ行い、商品が登録されている自販機のみ取得して自販機IDで照合する
	// ※ポップアップ表示後に商品名を取得すると後から表示が追加されるため、事前に付与しておく
	const attachProductNames = async (list) => {
		if (!Array.isArray(list) || !list.length) return;
		const targets = list.filter(item => !PRODUCT_NAME_FIELDS.some(field => field in item));
		if (!targets.length) return;

		const bounds = new google.maps.LatLngBounds();
		targets.forEach(item => bounds.extend(new google.maps.LatLng(Number(item.latitude), Number(item.longitude))));
		const center = bounds.getCenter();
		const radius = Math.ceil(targets.reduce((max, item) => Math.max(max,
			google.maps.geometry.spherical.computeDistanceBetween(center, new google.maps.LatLng(Number(item.latitude), Number(item.longitude)))
		), 0)) + 100;

		const query = `
			query GetProductNamesInArea($lon: numeric!, $lat: numeric!, $radius: Int!) {
				search_vending_machines(
					args: { p_longitude: $lon, p_latitude: $lat, p_max: $radius }
					where: { product_name1: { _is_null: false } }
				) {
					vending_machine_id
					${PRODUCT_NAME_FIELDS.join('\n\t\t\t\t\t')}
				}
			}
		`;
		try {
			const data = await fetchGraphQL(query, { lon: center.lng(), lat: center.lat(), radius });
			const namesById = {};
			(data.search_vending_machines || []).forEach(machine => {
				namesById[normalizeMachineId(machine.vending_machine_id)] = machine;
			});
			targets.forEach(item => {
				const machine = namesById[normalizeMachineId(item.vending_machine_id)];
				PRODUCT_NAME_FIELDS.forEach(field => {
					item[field] = machine ? machine[field] : null;
				});
			});
		} catch (error) {
			// 取得に失敗した場合は付与しない（ポップアップ表示時に個別取得する）
			console.error('商品名の一括取得に失敗しました:', error);
		}
	};
	// ポップアップの「商品」行のHTML
	const getProductRowHTML = (productNames) => `<dl class="jihanki-map-popup-item"><dt>商品</dt><dd><ul class="__products">${productNames.map(name => `<li>${name}</li>`).join('')}</ul></dd></dl>`;

	// 自販機タイプ（type1〜3）・商品に値が入っているレコードをコンソール出力（まとめて1回）
	const logRecordsWithTypeOrProduct = (list) => {
		if (!Array.isArray(list)) return;
		const total = list.length;

		// vending_machine_type1〜3 が「0以外（値あり）」のレコード
		const withType = list
			.filter(item => MACHINE_TYPE_FIELDS.some(field => hasMeaningfulValue(item[field])))
			.map(item => ({
				vending_machine_id: item.vending_machine_id,
				prefecture: item.prefecture,
				city: item.city,
				vending_machine_type1: item.vending_machine_type1,
				vending_machine_type2: item.vending_machine_type2,
				vending_machine_type3: item.vending_machine_type3,
			}));
		if (withType.length) {
			// console.log(`[自販機の種類あり] ${withType.length}件 / 全${total}件`, withType);
		} else {
			// console.log(`[自販機の種類あり] 0件 / 全${total}件（vending_machine_type1〜3 はすべて0または空）`);
		}

		// product_name1〜10 に値が入っているレコード
		const withProduct = list
			.filter(item => PRODUCT_NAME_FIELDS.some(field => hasMeaningfulValue(item[field])))
			.map(item => {
				const products = {};
				PRODUCT_NAME_FIELDS.forEach(field => {
					if (hasMeaningfulValue(item[field])) products[field] = item[field];
				});
				return {
					vending_machine_id: item.vending_machine_id,
					prefecture: item.prefecture,
					city: item.city,
					...products,
				};
			});
		// product_name1〜10 フィールド自体が取得結果に含まれているか
		// （現在地検索 search_vending_machines のみ返す。都道府県・市区町村検索の vending_machines テーブルには列が存在しない）
		const hasProductFields = list.some(item => PRODUCT_NAME_FIELDS.some(field => field in item));
		if (!hasProductFields) {
			// console.log(`[商品名] 商品名フィールド未取得のデータです（全${total}件）。商品データの確認は現在地検索（search_vending_machines）で行ってください`);
		} else if (withProduct.length) {
			// console.log(`[商品名あり] ${withProduct.length}件 / 全${total}件`, withProduct);
			// console.table(withProduct);
		} else {
			// console.log(`[商品名あり] 0件 / 全${total}件（product_name1〜10 はすべて空＝商品データ未登録）`);
		}

		// 取得した全レコードの中身（APIが返した全項目）
		// console.groupCollapsed(`[取得データ一覧] 全${total}件`);
		// console.table(list);
		// console.log(list);
		// console.groupEnd();
	};

	// URLの絞り込み条件（検索対象・自販機の種類・商品）で結果を絞り込む
	const applyResultFilters = (list) => {
		if (!Array.isArray(list)) return [];
		const urlParams = new URLSearchParams(window.location.search);
		const hasJihanki = urlParams.get('jihanki') === 'true';
		const hasBase = urlParams.get('base') === 'true';
		// 選択された種類フラグ（例: ?vending_machine_type1=1&vending_machine_type3=1）
		const selectedTypeFields = MACHINE_TYPE_FIELDS.filter(field => urlParams.get(field) === '1');
		const selectedProducts = urlParams.getAll('product');

		let result = list;

		// 検索対象（自販機/拠点）
		if (hasJihanki && !hasBase) {
			result = result.filter(item => !item.base);
		} else if (!hasJihanki && hasBase) {
			result = result.filter(item => item.base);
		}

		// 自販機の種類・商品（URLの product=商品ID）
		// 種類・商品とも複数選択は OR。種類と商品の両方を選択した場合も「どちらかに当てはまる」自販機を表示
		if (selectedTypeFields.length || selectedProducts.length) {
			result = result.filter(item =>
				selectedTypeFields.some(field => isTypeFlagOn(item[field]))
				|| (selectedProducts.length > 0 && hasSelectedProduct(item, selectedProducts))
			);
		}

		return result;
	};

	// 検索結果一覧から店舗情報を取得しマーカーを立てる
	let currentMarkers = []; // 現在のマーカーを保持
	let activeMarker = null; // クリック中（ポップアップ表示中）のマーカー

	// マーカーアイコンのサイズ（通常／クリック中は少し大きく表示）
	const MARKER_SIZE = 36;
	const MARKER_SIZE_ACTIVE = 44;

	// マーカーのアイコンを通常／強調（クリック中）に切り替える
	// ※アンカーは「通常サイズの中心が位置座標の18px上」に揃え、拡大しても円の中心がずれないようにする
	const setMarkerIcon = (marker, active) => {
		if (!marker) return;
		const size = active ? MARKER_SIZE_ACTIVE : MARKER_SIZE;
		marker.setIcon({
			url: marker.__iconUrl,
			scaledSize: new google.maps.Size(size, size),
			anchor: new google.maps.Point(size / 2, size / 2 + MARKER_SIZE / 2),
		});
		marker.setZIndex(active ? 1000 : 1);
	};

	// クリック中マーカーの強調を解除
	const clearActiveMarker = () => {
		if (activeMarker) setMarkerIcon(activeMarker, false);
		activeMarker = null;
	};

	const setMarker = async (_map, graphqlData = null) => {
		// 商品の絞り込み用に、選択商品を扱う自販機IDを取得（applyResultFilters で参照）
		await loadSelectedProductMachineIds();

		// 既存のマーカーをクリア
		currentMarkers.forEach(marker => marker.setMap(null));
		currentMarkers = [];
		activeMarker = null;

		// GraphQLでデータを取得してマーカーを設定
		const setupMarkers = async (_map, $result) => {
			// 自販機タイプ・商品に実データが入っているレコードをコンソール出力
			logRecordsWithTypeOrProduct($result);

			// 検索対象・自販機の種類・商品で絞り込み
			const filteredResult = applyResultFilters($result);
			let latLngBounds = new google.maps.LatLngBounds();

			// 同じ緯度経度の出現回数をカウント
			const locationCount = {};

			for(let i = 0; i < filteredResult.length; i++) {
				const lat = Number(filteredResult[i].latitude);
				const lng = Number(filteredResult[i].longitude);
				const key = `${lat},${lng}`;
				
				// 同じ位置のマーカー数をカウント
				if (!locationCount[key]) {
					locationCount[key] = 0;
				}
				
				// 同じ位置のマーカーには微小なオフセットを追加
				const offset = locationCount[key] * 0.000005; // 約0.5m程度のオフセット
				const angle = locationCount[key] * (Math.PI * 2 / 8); // 8方向に分散
				const offsetLat = lat + (Math.cos(angle) * offset);
				const offsetLng = lng + (Math.sin(angle) * offset);
				
				locationCount[key]++;
				
				let markerLatLng = new google.maps.LatLng({
					lat: offsetLat,
					lng: offsetLng
				});
				latLngBounds.extend(markerLatLng);
				// ※クリック時に拡大表示するため、拡大しても滲まないSVGを使用（webp版は36px固定でぼやける）
				let markerIconPath = '/images/jihanki/map/jihanki.svg';
				if(filteredResult[i].base) {
					markerIconPath = '/images/jihanki/map/base.svg';
				}
				let markerIconZindex = 1;
				
				const marker = new google.maps.Marker({
					position: markerLatLng,
					map: _map,
					icon: {
						url: markerIconPath,
						scaledSize: new google.maps.Size(MARKER_SIZE, MARKER_SIZE),
						anchor: new google.maps.Point(MARKER_SIZE / 2, MARKER_SIZE),
					},
					zIndex: markerIconZindex,
				});
				marker.__iconUrl = markerIconPath; // 強調表示の切り替え用にアイコン画像を保持
				currentMarkers.push(marker); // マーカーを配列に追加

				// マーカークリックイベント
				marker.addListener('click', () => {
					document.querySelectorAll('.js-map .jihanki-map-popup').forEach(el => el.remove());
					// クリックした自販機のアイコンを少し大きく表示（前回の強調は解除）
					clearActiveMarker();
					setMarkerIcon(marker, true);
					activeMarker = marker;
					const mapContainer = document.querySelector('.js-map');
					if (mapContainer) {
						const record = filteredResult[i];
						const address = (record.prefecture || '') + (record.city || '') + (record.address || '住所なし');

						// 所在地（常に表示）
						let rows = `<dl class="jihanki-map-popup-item"><dt>所在地</dt><dd>${address}</dd></dl>`;
						// 自販機（種類フラグが立っている場合のみ表示）
						const machineLabels = getMachineTypeLabels(record);
						if (machineLabels.length) {
							rows += `<dl class="jihanki-map-popup-item"><dt>自販機</dt><dd>${machineLabels.join('、')}</dd></dl>`;
						}
						// 商品（product_name1〜10 に値がある場合のみ表示）
						const productNames = getProductNames(record);
						if (productNames.length) {
							rows += getProductRowHTML(productNames);
						}
						// 商品名を事前に付与できなかったレコード（一括取得の失敗時）のみ、表示後に個別取得して追加
						const needsProductFetch = !productNames.length && !PRODUCT_NAME_FIELDS.some(field => field in record);

						const popupHTML = `<div class="jihanki-map-popup">`
							+ `<button type="button" class="__close"><svg aria-hidden="true"><use href="/dydo_common/svg/sprite.svg#close"></use></svg></button>`
							+ rows
							+ `</div>`;
						mapContainer.insertAdjacentHTML('beforeend', popupHTML);
						const popup = mapContainer.querySelector('.jihanki-map-popup');
						if (popup) {
							const closeButton = popup.querySelector('.__close');
							if (closeButton) {
								closeButton.addEventListener('click', () => {
									popup.remove();
									clearActiveMarker(); // ポップアップを閉じたらアイコンを通常サイズに戻す
								});
							}
							if (needsProductFetch) {
								fetchProductNamesForMachine(record)
									.then(names => {
										// 取得中に別のピンが選択された（ポップアップが閉じた・差し替わった）場合は反映しない
										if (!names.length || !popup.isConnected) return;
										popup.insertAdjacentHTML('beforeend', getProductRowHTML(names));
									})
									.catch(error => console.error('商品名の取得に失敗しました:', error));
							}
						}
					}
				});		
			}

			// 緯度経度が同じ場合は一つのピンにまとめる場合
			// for(let i = 0; i < $result.length; i++) {
			// 	let markerLatLng = new google.maps.LatLng({
			// 		lat: Number($result[i].latitude),
			// 		lng: Number($result[i].longitude)
			// 	});
			// 	latLngBounds.extend(markerLatLng);
			// 	let markerIconPath = '/images/jihanki/map/jihanki.svg';
			// 	let markerIconZindex = 1;
			// 			const marker = new google.maps.Marker({
			// 				position: markerLatLng,
			// 				map: _map,
			// 				icon: new google.maps.MarkerImage(markerIconPath),
			// 				zIndex: markerIconZindex
			// 			});
			// 			currentMarkers.push(marker); // マーカーを配列に追加

			// 	// マーカークリックイベント
			// 	marker.addListener('click', () => {
			// 		document.querySelectorAll('.js-map .jihanki-map-popup').forEach(el => el.remove());
			// 		const mapContainer = document.querySelector('.js-map');
			// 		if (mapContainer) {
			// 			const popupHTML = componentsPopup.replace('{!address!}', $result[i].address || '住所なし');
			// 			mapContainer.insertAdjacentHTML('beforeend', popupHTML);
			// 			const popup = mapContainer.querySelector('.jihanki-map-popup');
			// 			if (popup) {
			// 				const closeButton = popup.querySelector('.__close');
			// 				if (closeButton) {
			// 					closeButton.addEventListener('click', () => {
			// 						popup.remove();
			// 					});
			// 				}
			// 				}
			// 			}
			// 		});
			// }
		};

		// 外部からデータが渡された場合はそれを使用
		if (graphqlData && graphqlData.vending_machines) {
			const results = graphqlData.vending_machines;
			// 商品名を持たないレコード（住所検索など）に、ポップアップ用の商品名を付与
			await attachProductNames(results);
			await setupMarkers(_map, results);
			// フィルタリング後のピン数を計算して表示
			const pinCountElement = document.querySelector('.js-pin-count');
			if (pinCountElement) {
				pinCountElement.textContent = applyResultFilters(results).length;
			}
			return;
		}

		// パラメータ (p_lat, p_lon, p_max) がある場合のみ自動取得
		if(thisLat !== undefined && thisLon !== undefined && thisMax !== undefined) {
			try {
				const data = await searchVendingMachinesByLocation(thisLon, thisLat, thisMax);
				const results = data.search_vending_machines || [];
				await setupMarkers(_map, results);
				// ピン数を表示
				const pinCountElement = document.querySelector('.js-pin-count');
				if (pinCountElement) {
					pinCountElement.textContent = applyResultFilters(results).length;
				}
			} catch (error) {
				console.error('Error fetching vending machines by location:', error);
			}
		}
		// パラメータがない場合は何もしない（マーカーなし）
	};

	// マップ作成
	const setMap = () => {	
		const googleCliKey = 'AIzaSyC-EpRBOsXqkSMak-hSfMacX1KzM0672nQ'; // ダイドー
		// const googleCliKey = 'AIzaSyBKPDK9OwwBa-z5EDpvuxGUJ5heLAfHxAQ'; // テスト用
		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.src = `//maps.googleapis.com/maps/api/js?key=${googleCliKey}&v=quarterly&libraries=geometry&loading=async&callback=__jihankiInitMap`;
		script.id = 'googleApiScript';
		script.async = true;

		// 県コード
		let firstItem = '';
		
		// GraphQLでデータ取得（パラメータがある場合のみ）
		const fetchInitialData = async () => {
			try {
				let data;
				let results = [];

				// 近傍検索の場合のみデータを取得
				if(thisLat !== undefined && thisLon !== undefined && thisMax !== undefined) {
					data = await searchVendingMachinesByLocation(thisLon, thisLat, thisMax);
					results = data.search_vending_machines || [];
					
					// 最初のアイテムを取得
					firstItem = results[0];

					// 0件の場合
					if(!results.length) {
						const msg0 = document.querySelector('.js-0-msg');
						const msgResult = document.querySelector('.js-result-msg');
						const mapBlock = document.querySelector('.js-map');

						if(msg0 && msgResult && mapBlock) {
							mapBlock.classList.add('di_none');
							msg0.classList.remove('di_none');
							msgResult.classList.add('di_none');
						}
						return;
					}
				}
				// パラメータがない場合は何もしない（firstItemはundefinedのまま）
			} catch (error) {
				console.error('Error fetching initial data:', error);
				return;
			}
		};

		fetchInitialData()
			.then(() => {
				// loading=async では API 準備完了後に callback が呼ばれる（script.onload では未準備のため）
				window.__jihankiInitMap = () => {
					const canvas = document.querySelector('#js-map-canvas');

					// マップ要素が存在しない場合は処理を中断
					if (!canvas) {
						console.warn('.js-map-canvas element not found');
						return;
					}

					const styles = [
						{
							stylers: [{ saturation: -50 }]
						},
						{
							'featureType': 'landscape',
							'elementType': 'geometry.fill',
							'stylers': [{ 'color': '#efefe4' }]
						},
						{
							'featureType': 'water',
							'stylers': [{ 'color': '#bbc1d7' }]
						}
					];

					let mapLatLng;
					if(thisLat !== undefined && thisLon !== undefined) {
						// 近傍検索
						mapLatLng = new google.maps.LatLng({ lat: thisLat, lng: thisLon });
					} else if(firstItem) {
						// 県別
						mapLatLng = new google.maps.LatLng({
							lat: Number(firstItem.latitude),
							lng: Number(firstItem.longitude)
						});
					} else {
						// デフォルト位置（東京駅）
						// mapLatLng = new google.maps.LatLng({ lat: 35.6812, lng: 139.7671 });
					}
					
					
					const map = new google.maps.Map(canvas, {
						center: mapLatLng,
						zoom: thisZoom,
						styles,
						mapTypeControl: false,
						fullscreenControl: false,
						streetViewControl: false
					});

					currentMap = map; // グローバル変数に保存

					setMarker(map);

					// 位置情報ONのとき、現在位置がMAP内ならピンを表示
					const getCurrentPos = (pos) => {
						const currentMarkerLatLng = new google.maps.LatLng({
							lat: pos.coords.latitude,
							lng: pos.coords.longitude
						});
						const currentMarker = new google.maps.Marker({
							position: currentMarkerLatLng,
							map: map,
							icon: new google.maps.MarkerImage(
								currentPin,
								new google.maps.Size(36, 42),
								new google.maps.Point(0, 0),
								new google.maps.Point(9, 9)
							)
						});
					};
					navigator.geolocation.getCurrentPosition(getCurrentPos);

					// 地図ドラッグ時・ズームインアウト時に再検索
					let isSearching = false; // 検索中フラグ

					map.addListener('idle', () => {
						// すでに検索中の場合はスキップ
						if (isSearching) return;

						// マップの表示範囲（境界）を取得
						const bounds = map.getBounds();
						if (!bounds) return;
						const center = bounds.getCenter();

						// 明示検索直後（fitBounds由来）の自動再検索を1回抑止
						if (suppressNextIdleSearch) {
							suppressNextIdleSearch = false;
							lastSearchCenter = center;
							return;
						}

						const idleParams = new URLSearchParams(window.location.search);
						const selectedTypeFields = MACHINE_TYPE_FIELDS.filter(field => idleParams.get(field) === '1');

						const idleProductIds = idleParams.getAll('product').filter(Boolean);
						const useIdleProducts = idleProductIds.length > 0;

						// 種類・商品フィルタが有効なとき：表示範囲内の該当自販機を検索して表示（該当は少数のためズーム制限なし＝広域でも表示）
						// ※種類と商品の両方を選択した場合は「どちらかに当てはまる」自販機を表示するため、両方の結果を結合する
						if (selectedTypeFields.length || useIdleProducts) {
							(async () => {
								isSearching = true;
								lastSearchCenter = center;
								try {
									const [typeList, productList] = await Promise.all([
										selectedTypeFields.length
											? searchVendingMachinesByBoundsAndTypes(bounds.getSouthWest(), bounds.getNorthEast(), selectedTypeFields)
											: Promise.resolve(null),
										useIdleProducts ? getProductMachines(idleProductIds) : Promise.resolve(null),
									]);
									// 商品の該当自販機は全国分を保持しているため、表示範囲内のものに限定
									const productInBounds = productList
										? productList.filter(item => bounds.contains(new google.maps.LatLng(Number(item.latitude), Number(item.longitude))))
										: null;
									const list = mergeMachineLists(typeList, productInBounds);
									await setMarker(map, { vending_machines: list });
								} catch (error) {
									console.error('Error updating markers (bounds+type):', error);
								} finally {
									isSearching = false;
								}
							})();
							return;
						}

						// フィルタなし：従来どおり（広域表示のときはスキップ、半径3kmの近傍再検索）
						if (map.getZoom() < 11) return;
						if (!lastSearchCenter) {
							lastSearchCenter = center;
							return;
						}
						const distance = google.maps.geometry.spherical.computeDistanceBetween(lastSearchCenter, center);
						if (distance >= 500) {
							(async () => {
								isSearching = true;
								const newLat = center.lat();
								const newLng = center.lng();
								thisLat = newLat;
								thisLon = newLng;
								lastSearchCenter = center;
								try {
									const data = await searchVendingMachinesByLocation(newLng, newLat, 3000);
									const vendingMachines = data.search_vending_machines || [];
									// console.log('ドラッグ更新: ' + vendingMachines.length + '件');
									await setMarker(map, { vending_machines: vendingMachines });
								} catch (error) {
									console.error('Error updating markers after drag:', error);
								} finally {
									isSearching = false;
								}
							})();
						}
					});

					// map.addListener('dragend', () => {
					// 	let newLatLng = map.getCenter();
						
					// 	// 最後の検索位置からの距離を計算
					// 	const distance = google.maps.geometry.spherical.computeDistanceBetween(
					// 		lastSearchCenter,
					// 		newLatLng
					// 	);
						
					// 	// 検索半径の3/4以上移動していたら再検索
					// 	const radius = thisMax || 30000;
					// 	const threshold = radius * 0.75;
						
					// 	if (distance >= threshold) {
					// 		(async () => {
					// 			const newLat = newLatLng.lat();
					// 			const newLng = newLatLng.lng();

					// 			// マップの中心座標を更新
					// 			thisLat = newLat;
					// 			thisLon = newLng;
					// 			lastSearchCenter = newLatLng; // 検索位置を更新

					// 			try {
					// 				const data = await searchVendingMachinesByLocation(newLng, newLat, radius);
					// 				const vendingMachines = data.search_vending_machines || [];
					// 				await setMarker(map, { vending_machines: vendingMachines });
					// 			} catch (error) {
					// 				console.error('Error updating markers after drag:', error);
					// 			}
					// 		})();
					// 	}
					// });


					// マップの初期化完了後、URLパラメータがあれば処理
					initializeFromUrlParams();
				};
				document.body.appendChild(script);
			})
			.catch(error => console.error('Error fetching shop data:', error));
	};

	// 現在地から探すボタン
	const searchLocation = () => {
		// サロン検索TOP
		const topLocationButtons = document.querySelector('.js-location-btn');
		if (topLocationButtons) {
			topLocationButtons.addEventListener('click', function() {
				const getPosSuccess = (pos) => {
					location.href = `${$path}?p_lat=${pos.coords.latitude}&p_lon=${pos.coords.longitude}&p_max=3000`;
				};
				const getPosError = () => {
					alert('位置情報を取得できませんでした。');
				};
				navigator.geolocation.getCurrentPosition(getPosSuccess, getPosError);
			});
		}
	};

	// 都道府県selectのイベントリスナーを設定
	const setupPrefectureSelect = () => {
		const selectElement = document.querySelector('.js-prefecture-select');
		if (!selectElement) return;
		// 都道府県selectを初期化（prefecturesを取得して表示）
		// const populatePrefectureSelect = async () => {
		// 	try {
		// 		const prefectures = await getPrefectures();
		// 		prefectures.forEach(prefecture => {
		// 			const option = document.createElement('option');
		// 			option.value = prefecture;
		// 			option.textContent = prefecture;
		// 			selectElement.appendChild(option);
		// 		});
		// 	} catch (error) {
		// 		console.error('都道府県の取得に失敗しました:', error);
		// 	}
		// };

		// populatePrefectureSelect();

		// 都道府県selectのchangeイベントリスナーを追加
		selectElement.addEventListener('change', async (e) => {
			const selectedPrefecture = e.target.value;
			const citySelect = document.querySelector('.js-city-select');

			if (!citySelect) return;

			// 市区町村selectをリセット
			const firstOption = citySelect.querySelector('option:first-child');
			citySelect.innerHTML = '';
			if (firstOption) {
				citySelect.appendChild(firstOption.cloneNode(true));
			}

			// 選択した都道府県に存在しない種類を選択不可にする（未選択なら全種類選択可）
			updateTypeAvailabilityForArea(true);

			// 都道府県が選択されていない場合は終了
			if (!selectedPrefecture) return;

			// 都道府県が選ばれたら「住所から探す」を選択状態にする
			applySearchMode('address');

			try {
				const cities = await getCitiesByPrefecture(selectedPrefecture);
				// 標準地域コード順にソート
				const sortedCities = await sortCitiesByCode(selectedPrefecture, cities);
				
				// 市区町村のoptionを追加
				sortedCities.forEach(city => {
					const option = document.createElement('option');
					option.value = city;
					option.textContent = city;
					citySelect.appendChild(option);
				});
				// 種類フィルタに該当が無い市区町村は選択不可にする
				applyCityAvailability(true);
			} catch (error) {
				console.error('市区町村の取得に失敗しました:', error);
			}
		});

		// 市区町村を選んだら、そのエリアに存在しない種類を選択不可にする＋「住所から探す」を選択状態に
		const citySelectEl = document.querySelector('.js-city-select');
		if (citySelectEl) {
			citySelectEl.addEventListener('change', () => {
				if (citySelectEl.value) applySearchMode('address');
				updateTypeAvailabilityForArea(true);
			});
		}
	};

	// マップ上のマーカーを更新する
	const updateMapWithFilter = async (prefecture, city = null) => {
		if (!currentMap) {
			console.warn('Map instance not found');
			return;
		}

		try {
			let data;
			let results = [];

			if (city) {
				// 都道府県と市区町村で絞り込み
				// console.log('Filtering by prefecture and city:', prefecture, city);
				data = await searchVendingMachinesByPrefectureAndCity(prefecture, city);
				results = data.vending_machines || [];
			} else if (prefecture) {
				// 都道府県のみで絞り込み
				// console.log('Filtering by prefecture only:', prefecture);
				data = await searchVendingMachinesByPrefecture(prefecture);
				results = data.vending_machines || [];
			}

			// console.log(`Found ${results.length} vending machines for display`);

			// ※fitBounds由来のidle自動再検索を抑止して該当ピンが消えるのを防ぐ
			suppressNextIdleSearch = true;

			if (results.length === 0) {
				// 都道府県内に自販機が無い場合でも、マップは表示（日本全体を表示）
				currentMap.setCenter(new google.maps.LatLng(JAPAN_CENTER.lat, JAPAN_CENTER.lng));
				currentMap.setZoom((FUNCTIONS.va.window.width > 768) ? 5 : 4);
				return;
			}

			// 既存のマーカーをクリアして新しいマーカーを追加（種類・商品の絞り込みはsetMarker内で適用）
			await setMarker(currentMap, { vending_machines: results });

			// 絞り込み後の該当自販機
			const filtered = applyResultFilters(results);
			// 「自販機の種類」「商品」の絞り込みが有効かどうか
			const filterParams = new URLSearchParams(window.location.search);
			const hasTypeOrProductFilter = MACHINE_TYPE_FIELDS.some(field => filterParams.get(field) === '1')
				|| filterParams.getAll('product').length > 0;

			if (filtered.length === 0) {
				// 種類・商品の絞り込みで該当0件でも、検索した都道府県の範囲でマップは表示（ピンなし）
				fitMapToResults(currentMap, results);
				return;
			}

			if (!hasTypeOrProductFilter) {
				// 種類・商品フィルタなし：従来のズーム率（重心を中心に半径3km）
				let sumLat = 0;
				let sumLng = 0;
				filtered.forEach(result => {
					sumLat += Number(result.latitude);
					sumLng += Number(result.longitude);
				});
				setBoundsFromCenter(currentMap, sumLat / filtered.length, sumLng / filtered.length, 3000);
			} else if (filtered.length === 1) {
				// 該当1件のときは寄りすぎないよう半径3kmで表示
				setBoundsFromCenter(currentMap, Number(filtered[0].latitude), Number(filtered[0].longitude), 3000);
			} else {
				// 種類・商品フィルタあり：該当ピンが全て収まるように表示
				fitMapToResults(currentMap, filtered);
			}
		} catch (error) {
			console.error('Error updating map with filter:', error);
			alert('データの取得に失敗しました。');
		}
	};

	// 絞り込みパラメータ（検索対象・自販機の種類・商品）をURL用に組み立て
	// ※ 現在地・住所どちらの検索でも共通で付与する
	const buildFilterParams = () => {
		let params = '';
		const jihankiCheckbox = document.getElementById('jihanki');
		const baseCheckbox = document.getElementById('base');
		// 検索対象
		if (jihankiCheckbox && jihankiCheckbox.checked) {
			params += '&jihanki=true';
		}
		if (baseCheckbox && baseCheckbox.checked) {
			params += '&base=true';
		}
		// 自販機の種類（例: &vending_machine_type1=1&vending_machine_type3=1）
		document.querySelectorAll('.js-filter-mtype:checked').forEach(cb => {
			params += `&${cb.name}=1`;
		});
		// 商品（例: &product=11041 ※value=商品ID）
		document.querySelectorAll('.js-filter-product:checked').forEach(cb => {
			if (cb.value) {
				params += `&product=${encodeURIComponent(cb.value)}`;
			}
		});
		return params;
	};

	// 検索モードを適用（ボタンのアクティブ表示・セレクトの活性/非活性・検索ボタン有効化）
	const applySearchMode = (mode) => {
		searchMode = mode;

		const currentBtn = document.querySelector('.jihanki-map-search-location');
		const addressBtn = document.querySelector('.js-map-address');
		const prefectureSelect = document.querySelector('.js-prefecture-select');
		const citySelect = document.querySelector('.js-city-select');
		const submitButton = document.querySelector('.js-map-submit');

		// ボタンのアクティブ表示
		if (currentBtn) currentBtn.classList.toggle('is-active', mode === 'current');
		if (addressBtn) addressBtn.classList.toggle('is-active', mode === 'address');

		// 現在地モードは都道府県・市区町村を非活性（グレーアウト）
		const disableSelects = (mode === 'current');
		[prefectureSelect, citySelect].forEach(sel => {
			if (!sel) return;
			sel.disabled = disableSelects;
			const wrap = sel.closest('.__select');
			if (wrap) wrap.classList.toggle('is-disabled', disableSelects);
		});

		// 現在地から探す：都道府県・市区町村群の選択状態をリセット
		if (disableSelects) {
			if (prefectureSelect) prefectureSelect.value = '';
			resetCitySelect();
			// 都道府県がクリアされたので、種類の選択可否を全解除（全種類選択可）
			updateTypeAvailabilityForArea(true);
		}

		// モードが選択されたら検索ボタンを有効化
		if (submitButton && mode) {
			submitButton.classList.remove('is-disable');
		}

		// 選択解除ボタンの活性/非活性を更新
		updateClearButtonState();
	};

	// 現在地／住所のモード選択ボタン
	const setupModeButtons = () => {
		const currentBtn = document.querySelector('.jihanki-map-search-location');
		const addressBtn = document.querySelector('.js-map-address');
		if (currentBtn) currentBtn.addEventListener('click', () => applySearchMode('current'));
		if (addressBtn) addressBtn.addEventListener('click', () => applySearchMode('address'));
	};

	// 検索条件（モード・都道府県・市区町村・検索対象・種類・商品）が1つでも選択されているか
	const hasAnySelection = () => {
		if (searchMode) return true;
		const prefectureSelect = document.querySelector('.js-prefecture-select');
		const citySelect = document.querySelector('.js-city-select');
		if (prefectureSelect && prefectureSelect.value) return true;
		if (citySelect && citySelect.value) return true;
		if (['jihanki', 'base'].some(id => {
			const cb = document.getElementById(id);
			return cb && cb.checked;
		})) return true;
		if (document.querySelector('.js-filter-mtype:checked')) return true;
		if (document.querySelector('.js-filter-product:checked')) return true;
		return false;
	};

	// 選択解除ボタンの活性/非活性を更新（何も選択されていないときは非活性）
	const updateClearButtonState = () => {
		const clearButton = document.querySelector('.js-map-clear');
		if (!clearButton) return;
		const active = hasAnySelection();
		clearButton.disabled = !active;
		clearButton.classList.toggle('is-disable', !active);
	};

	// 選択解除ボタン：検索条件をすべて初期状態に戻す
	const setupClearButton = () => {
		const clearButton = document.querySelector('.js-map-clear');
		if (!clearButton) return;

		// 各条件の変更で活性/非活性を更新
		document.querySelectorAll('.js-filter-mtype, .js-filter-product, #jihanki, #base, .js-prefecture-select, .js-city-select').forEach(el => {
			el.addEventListener('change', updateClearButtonState);
		});

		clearButton.addEventListener('click', () => {
			// 検索モードを解除（両ボタンの選択状態を外し、検索ボタンを無効化）
			searchMode = null;
			const currentBtn = document.querySelector('.jihanki-map-search-location');
			const addressBtn = document.querySelector('.js-map-address');
			const submitButton = document.querySelector('.js-map-submit');
			if (currentBtn) currentBtn.classList.remove('is-active');
			if (addressBtn) addressBtn.classList.remove('is-active');
			if (submitButton) submitButton.classList.add('is-disable');

			// 都道府県・市区町村群をリセットして活性に戻す
			const prefectureSelect = document.querySelector('.js-prefecture-select');
			const citySelect = document.querySelector('.js-city-select');
			if (prefectureSelect) prefectureSelect.value = '';
			resetCitySelect();
			[prefectureSelect, citySelect].forEach(sel => {
				if (!sel) return;
				sel.disabled = false;
				const wrap = sel.closest('.__select');
				if (wrap) wrap.classList.remove('is-disabled');
			});

			// 検索対象（自販機/拠点）を外す
			['jihanki', 'base'].forEach(id => {
				const cb = document.getElementById(id);
				if (cb) cb.checked = false;
			});

			// 自販機の種類を外す（カード表示も同期）
			document.querySelectorAll('.js-filter-mtype').forEach(checkbox => {
				checkbox.checked = false;
				syncMachineTypeCard(checkbox);
			});

			// 商品を外す
			document.querySelectorAll('.js-filter-product').forEach(checkbox => {
				checkbox.checked = false;
			});

			// 選択可否（disabled）をすべて解除
			matchedPrefectureSet = null;
			matchedCityMap = null;
			applyPrefectureAvailability(false);
			applyCityAvailability(false);
			applyTypeAvailability(null, false);
			applyProductAvailability(null, false);

			// すべて解除されたので選択解除ボタン自身を非活性に
			updateClearButtonState();
		});
	};

	// 検索ボタンのイベントリスナー（現在地／住所を統合）
	const setupSearchButton = () => {
		const searchButton = document.querySelector('.js-map-submit');
		if (!searchButton) return;

		searchButton.addEventListener('click', () => {
			if (!searchMode) {
				alert('「現在地から探す」または「住所から探す」を選択してください。');
				return;
			}

			// 現在地から探す：位置情報を取得してから遷移
			if (searchMode === 'current') {
				const getPosSuccess = (pos) => {
					const url = `${location.pathname}?p_lat=${pos.coords.latitude}&p_lon=${pos.coords.longitude}&p_max=3000` + buildFilterParams();
					location.href = url;
				};
				const getPosError = () => {
					alert('位置情報を取得できませんでした。位置情報の使用を許可してください。');
				};
				navigator.geolocation.getCurrentPosition(getPosSuccess, getPosError);
				return;
			}

			// 住所から探す
			const prefectureSelect = document.querySelector('.js-prefecture-select');
			const citySelect = document.querySelector('.js-city-select');
			const selectedPrefecture = prefectureSelect ? prefectureSelect.value : '';
			const selectedCity = citySelect ? citySelect.value : '';

			let url = `${location.pathname}?`;
			if (selectedPrefecture) {
				url += `p_prefectures=${encodeURIComponent(selectedPrefecture)}`;
				if (selectedCity) {
					url += `&city=${encodeURIComponent(selectedCity)}`;
				}
			} else {
				// 都道府県未選択：全国検索
				url += 'area=all';
			}
			url += buildFilterParams();

			location.href = url;
		});
	};

	// URLパラメータから検索モードの表示状態を復元
	const initializeSearchMode = () => {
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.get('p_lat') && urlParams.get('p_lon')) {
			applySearchMode('current');
		} else if (urlParams.get('p_prefectures') || urlParams.get('area') === 'all') {
			applySearchMode('address');
		}
	};

	// 自販機の種類カードの選択状態（見た目）を同期
	const syncMachineTypeCard = (checkbox) => {
		const card = checkbox.closest('.jihanki-map-search-card');
		if (card) {
			card.classList.toggle('is-checked', checkbox.checked);
		}
	};

	// 市区町村セレクトを初期状態（先頭option）に戻す
	const resetCitySelect = () => {
		const citySelect = document.querySelector('.js-city-select');
		if (!citySelect) return;
		const first = citySelect.querySelector('option:first-child');
		citySelect.innerHTML = '';
		if (first) citySelect.appendChild(first.cloneNode(true));
	};

	// 都道府県セレクトの選択肢の活性/非活性を更新（該当が無い都道府県は選択不可）
	const applyPrefectureAvailability = (resetInvalid = true) => {
		const prefectureSelect = document.querySelector('.js-prefecture-select');
		if (!prefectureSelect) return;
		Array.from(prefectureSelect.options).forEach(option => {
			if (!option.value) { option.disabled = false; return; } // 「選択してください」
			option.disabled = matchedPrefectureSet ? !matchedPrefectureSet.has(option.value) : false;
		});
		const current = prefectureSelect.options[prefectureSelect.selectedIndex];
		if (resetInvalid && prefectureSelect.value && current && current.disabled) {
			prefectureSelect.value = '';
			resetCitySelect();
		}
	};

	// 市区町村セレクトの選択肢の活性/非活性を更新（該当が無い市区町村は選択不可）
	const applyCityAvailability = (resetInvalid = true) => {
		const citySelect = document.querySelector('.js-city-select');
		const prefectureSelect = document.querySelector('.js-prefecture-select');
		if (!citySelect || !prefectureSelect) return;
		const citySet = matchedCityMap ? matchedCityMap[prefectureSelect.value] : null;
		Array.from(citySelect.options).forEach(option => {
			if (!option.value) { option.disabled = false; return; }
			option.disabled = matchedCityMap ? !(citySet && citySet.has(option.value)) : false;
		});
		const current = citySelect.options[citySelect.selectedIndex];
		if (resetInvalid && citySelect.value && current && current.disabled) {
			citySelect.value = '';
		}
	};

	// レコード一覧から、都道府県セットと都道府県ごとの市区町村セットを集計
	const buildAreaSets = (list) => {
		const prefectureSet = new Set();
		const cityMap = {};
		list.forEach(machine => {
			if (!machine.prefecture) return;
			prefectureSet.add(machine.prefecture);
			if (machine.city) {
				if (!cityMap[machine.prefecture]) cityMap[machine.prefecture] = new Set();
				cityMap[machine.prefecture].add(machine.city);
			}
		});
		return { prefectureSet, cityMap };
	};

	// 2つの地域集計の和（種類と商品の両方が選択されているときは「どちらかに当てはまる」自販機がある地域を選択可にする）
	const unionAreaSets = (a, b) => {
		const prefectureSet = new Set([...a.prefectureSet, ...b.prefectureSet]);
		const cityMap = {};
		prefectureSet.forEach(prefecture => {
			cityMap[prefecture] = new Set([...(a.cityMap[prefecture] || []), ...(b.cityMap[prefecture] || [])]);
		});
		return { prefectureSet, cityMap };
	};

	// 選択中の「自販機の種類」「商品」に該当する自販機がある都道府県／市区町村を集計し、選択肢の可否を更新
	// （該当する自販機が無い都道府県・市区町村は選択不可にする）
	let filterAvailabilityRequestId = 0; // 連続操作時に古いレスポンスで上書きされないよう最新リクエストのみ反映
	const updateFilterAvailability = async (resetInvalid = true) => {
		const requestId = ++filterAvailabilityRequestId;
		const selectedTypeFields = MACHINE_TYPE_FIELDS.filter(field => {
			const cb = document.getElementById(field);
			return cb && cb.checked;
		});
		const selectedProductIds = Array.from(document.querySelectorAll('.js-filter-product:checked'))
			.map(cb => cb.value)
			.filter(Boolean);

		if (!selectedTypeFields.length && !selectedProductIds.length) {
			// 種類・商品フィルタなし → すべて選択可
			matchedPrefectureSet = null;
			matchedCityMap = null;
			applyPrefectureAvailability(resetInvalid);
			applyCityAvailability(resetInvalid);
			return;
		}

		try {
			// 種類・商品それぞれの該当自販機を並行取得
			const [typeList, productList] = await Promise.all([
				selectedTypeFields.length ? searchVendingMachinesByTypes(selectedTypeFields) : Promise.resolve(null),
				selectedProductIds.length ? getAreasWithProducts(selectedProductIds) : Promise.resolve(null),
			]);
			if (requestId !== filterAvailabilityRequestId) return; // より新しいリクエストがあるので破棄

			let areas = null;
			if (typeList) {
				areas = buildAreaSets(typeList.filter(machine => selectedTypeFields.some(field => isTypeFlagOn(machine[field]))));
			}
			if (productList) {
				const productAreas = buildAreaSets(productList);
				areas = areas ? unionAreaSets(areas, productAreas) : productAreas;
			}
			matchedPrefectureSet = areas.prefectureSet;
			matchedCityMap = areas.cityMap;
			// console.log(`[絞り込み対応地域] 都道府県 ${matchedPrefectureSet.size}件（種類: ${selectedTypeFields.join(',') || '-'} / 商品ID: ${selectedProductIds.join(',') || '-'}）`);
			applyPrefectureAvailability(resetInvalid);
			applyCityAvailability(resetInvalid);
		} catch (error) {
			console.error('絞り込み（種類・商品）の対応地域取得に失敗しました:', error);
		}
	};
	// 「自販機の種類」チェックボックスの活性/非活性を更新（選択エリアに存在しない種類は選択不可）
	const applyTypeAvailability = (availableTypeSet, resetInvalid = true) => {
		MACHINE_TYPE_FIELDS.forEach(field => {
			const checkbox = document.getElementById(field);
			if (!checkbox) return;
			const card = checkbox.closest('.jihanki-map-search-card');
			const disabled = availableTypeSet ? !availableTypeSet.has(field) : false;
			checkbox.disabled = disabled;
			if (card) card.classList.toggle('is-disabled', disabled);
			// 選択エリアに存在しない種類がチェック済みなら外す
			if (resetInvalid && disabled && checkbox.checked) {
				checkbox.checked = false;
				syncMachineTypeCard(checkbox);
			}
		});
	};

	// 「商品」チェックボックスの活性/非活性を更新（選択エリアに扱う自販機が無い商品は選択不可）
	const applyProductAvailability = (availableProductIdSet, resetInvalid = true) => {
		document.querySelectorAll('.js-filter-product').forEach(checkbox => {
			const card = checkbox.closest('.jihanki-map-search-product');
			const disabled = availableProductIdSet ? !availableProductIdSet.has(checkbox.value) : false;
			checkbox.disabled = disabled;
			if (card) card.classList.toggle('is-disabled', disabled);
			// 選択エリアに扱う自販機が無い商品がチェック済みなら外す
			if (resetInvalid && disabled && checkbox.checked) {
				checkbox.checked = false;
			}
		});
	};

	// 商品ごとの「扱う自販機がある地域（都道府県・市区町村）」の一覧（商品ID → Promise<レコード配列>）
	const productAreaCache = {};
	const getProductAreas = (productId) => {
		if (!productAreaCache[productId]) {
			productAreaCache[productId] = getAreasWithProducts([productId]).catch(error => {
				delete productAreaCache[productId];
				throw error;
			});
		}
		return productAreaCache[productId];
	};
	// 指定エリア（都道府県／＋市区町村）に扱う自販機がある商品IDの集合を取得
	const getAvailableProductsForArea = async (prefecture, city) => {
		const productIds = getProductFilterOptions().map(option => option.id);
		const results = await Promise.all(productIds.map(id => getProductAreas(id)));
		const set = new Set();
		productIds.forEach((id, i) => {
			const found = (results[i] || []).some(machine =>
				machine.prefecture === prefecture && (!city || machine.city === city)
			);
			if (found) set.add(id);
		});
		return set;
	};

	// 選択中の都道府県／市区町村に応じて、「自販機の種類」「商品」の選択可否を更新
	let areaAvailabilityRequestId = 0; // 連続操作時に古いレスポンスで上書きされないよう最新リクエストのみ反映
	const updateTypeAvailabilityForArea = async (resetInvalid = true) => {
		const requestId = ++areaAvailabilityRequestId;
		const prefectureSelect = document.querySelector('.js-prefecture-select');
		const citySelect = document.querySelector('.js-city-select');
		const prefecture = prefectureSelect ? prefectureSelect.value : '';
		const city = citySelect ? citySelect.value : '';

		if (!prefecture) {
			// エリア未選択 → 種類・商品は全て選択可
			applyTypeAvailability(null, resetInvalid);
			applyProductAvailability(null, resetInvalid);
			return;
		}
		try {
			const [availableTypes, availableProducts] = await Promise.all([
				getAvailableTypesForArea(prefecture, city),
				getAvailableProductsForArea(prefecture, city),
			]);
			if (requestId !== areaAvailabilityRequestId) return;
			applyTypeAvailability(availableTypes, resetInvalid);
			applyProductAvailability(availableProducts, resetInvalid);
			// 種類のチェックが変わった場合は都道府県/市区町村側の可否も再評価（整合性維持）
			if (resetInvalid) updateFilterAvailability(false);
		} catch (error) {
			console.error('エリアの対応種類・商品の取得に失敗しました:', error);
		}
	};

	// 自販機の種類チェックボックスの変更を監視して見た目を同期＋対応地域の可否を更新
	const setupMachineTypeFilter = () => {
		document.querySelectorAll('.js-filter-mtype').forEach(checkbox => {
			syncMachineTypeCard(checkbox);
			checkbox.addEventListener('change', () => {
				syncMachineTypeCard(checkbox);
				updateFilterAvailability(true);
			});
		});
		// 商品チェックの変更でも対応地域の可否を再評価
		document.querySelectorAll('.js-filter-product').forEach(checkbox => {
			checkbox.addEventListener('change', () => updateFilterAvailability(true));
		});
	};

	// URLパラメータに基づいて絞り込み（自販機の種類・商品）の状態を復元
	const initializeFilterStates = () => {
		const urlParams = new URLSearchParams(window.location.search);
		const anyTypeSelected = MACHINE_TYPE_FIELDS.some(field => urlParams.get(field) === '1');
		const selectedProducts = urlParams.getAll('product');

		if (anyTypeSelected) {
			document.querySelectorAll('.js-filter-mtype').forEach(checkbox => {
				checkbox.checked = urlParams.get(checkbox.name) === '1';
				syncMachineTypeCard(checkbox);
			});
		}
		if (selectedProducts.length) {
			document.querySelectorAll('.js-filter-product').forEach(checkbox => {
				checkbox.checked = selectedProducts.includes(checkbox.value);
			});
		}
	};

	// URLパラメータに基づいてチェックボックスの初期状態を設定
	const initializeCheckboxStates = () => {
		const urlParams = new URLSearchParams(window.location.search);
		const hasJihanki = urlParams.get('jihanki') === 'true';
		const hasBase = urlParams.get('base') === 'true';

		const jihankiCheckbox = document.getElementById('jihanki');
		const baseCheckbox = document.getElementById('base');


		if(hasJihanki === false && hasBase === false) return; // どちらもない場合は初期状態のまま

		if (jihankiCheckbox) {
			jihankiCheckbox.checked = hasJihanki;
		}

		if (baseCheckbox) {
			baseCheckbox.checked = hasBase;
		}
	};


	// URLパラメータから都道府県・市区町村を取得して初期表示
	const initializeFromUrlParams = async () => {
		const urlParams = new URLSearchParams(window.location.search);
		const prefecture = urlParams.get('p_prefectures');
		const city = urlParams.get('city');
		const lat = urlParams.get('p_lat');
		const lon = urlParams.get('p_lon');
		const max = urlParams.get('p_max');
		const area = urlParams.get('area');

		// 緯度経度パラメータがある場合は現在地検索と同じ処理
		if (lat && lon && max) {
			// console.log('Initializing with location params - lat:', lat, 'lon:', lon, 'max:', max);

			try {
				const latitude = parseFloat(lat);
				const longitude = parseFloat(lon);
				const radius = parseInt(max, 10);

				const data = await searchVendingMachinesByLocation(longitude, latitude, radius);
				const results = data.search_vending_machines || [];

				// console.log(`Found ${results.length} vending machines from URL params`);

				if (results.length === 0) {
					alert('指定された位置周辺に自販機が見つかりませんでした。');
					return;
				}

				// マップが準備できていることを確認してからマーカーを表示
				if (currentMap) {
					await setMarker(currentMap, { vending_machines: results });

					// 中心点から半径3kmの範囲を表示
					setBoundsFromCenter(currentMap, latitude, longitude, 3000);

					document.querySelector('.js-map-wrap')?.classList.remove('di_none');
				} else {
					// マップがまだ準備できていない場合は、少し待ってから実行
					setTimeout(async () => {
						if (currentMap) {
							await setMarker(currentMap, { vending_machines: results });

							// 中心点から半径3kmの範囲を表示
							setBoundsFromCenter(currentMap, latitude, longitude, 3000);

							document.querySelector('.js-map-wrap')?.classList.remove('di_none');
						}
					}, 1000);
				}
			} catch (error) {
				console.error('Error fetching vending machines from URL params:', error);
				alert('データの取得に失敗しました。');
			}

			return; // 緯度経度検索の場合はここで終了
		}

		// 全国検索（都道府県未選択）
		if (area === 'all') {
			try {
				// 種類フィルタがある場合は、該当する自販機を全国から直接取得（サンプルには希少な該当が含まれないため）
				const selectedTypeFields = MACHINE_TYPE_FIELDS.filter(field => urlParams.get(field) === '1');
				const selectedProductIds = urlParams.getAll('product').filter(Boolean);
				// 商品の絞り込みがある場合も、選択商品を扱う自販機を全国から直接取得（サンプルには含まれないため）
				// 種類と商品の両方を選択した場合は「どちらかに当てはまる」自販機を表示するため、両方の取得結果を結合する
				const useProducts = selectedProductIds.length > 0;
				let dataset;
				if (selectedTypeFields.length || useProducts) {
					const [typeList, productList] = await Promise.all([
						selectedTypeFields.length ? searchVendingMachinesByTypes(selectedTypeFields) : Promise.resolve(null),
						useProducts ? getProductMachines(selectedProductIds) : Promise.resolve(null),
					]);
					dataset = mergeMachineLists(typeList, productList);
				} else {
					// フィルタなし：全国から分散抽出したサンプルを表示（以降は地図の移動・ズームで範囲検索）
					dataset = await getNationwideSample();
				}

				const plotNationwide = async (map) => {
					await setMarker(map, { vending_machines: dataset });
					// 絞り込み後の該当ピンが収まるように範囲を調整（該当がなければ日本全体を表示）
					const filtered = applyResultFilters(dataset);
					suppressNextIdleSearch = true;
					if (!fitMapToResults(map, filtered)) {
						map.setCenter(new google.maps.LatLng(JAPAN_CENTER.lat, JAPAN_CENTER.lng));
						map.setZoom((FUNCTIONS.va.window.width > 768) ? 5 : 4);
					}
					document.querySelector('.js-map-wrap')?.classList.remove('di_none');
				};

				if (currentMap) {
					await plotNationwide(currentMap);
				} else {
					setTimeout(async () => {
						if (currentMap) await plotNationwide(currentMap);
					}, 1000);
				}
			} catch (error) {
				console.error('Error fetching nationwide vending machines:', error);
				alert('データの取得に失敗しました。');
			}
			return;
		}

		// 都道府県パラメータがある場合
		if (prefecture) {

			// 都道府県selectに値を設定（少し待ってからpopulateが完了していることを確認）
			const prefectureSelect = document.querySelector('.js-prefecture-select');
			const citySelect = document.querySelector('.js-city-select');
			
			if (prefectureSelect) {
				// // populatePrefectureSelectが完了するまで少し待つ
				const waitForOptions = () => {
					return new Promise((resolve) => {
						const checkOptions = () => {
							if (prefectureSelect.options.length > 1) { // 最初のoption以外にもoptionがある
								resolve();
							} else {
								setTimeout(checkOptions, 100);
							}
						};
						checkOptions();
					});
				};

				await waitForOptions();

				// 都道府県を選択
				prefectureSelect.value = prefecture;

				// 市区町村のoptionを取得して設定
				if (citySelect) {
					try {
						// 市区町村selectをリセット
						const firstOption = citySelect.querySelector('option:first-child');
						citySelect.innerHTML = '';
						if (firstOption) {
							citySelect.appendChild(firstOption.cloneNode(true));
						}

						// 市区町村のoptionを取得
						const cities = await getCitiesByPrefecture(prefecture);
						// 標準地域コード順にソート
						const sortedCities = await sortCitiesByCode(prefecture, cities);
						
						// 市区町村のoptionを追加
						sortedCities.forEach(cityName => {
							const option = document.createElement('option');
							option.value = cityName;
							option.textContent = cityName;
							citySelect.appendChild(option);
						});

						// cityパラメータがある場合は選択
						if (city) {
							citySelect.value = city;
						}
						// 種類フィルタに該当が無い市区町村は選択不可にする（復元した選択はリセットしない）
						applyCityAvailability(false);
						// 復元した都道府県／市区町村に存在しない種類を選択不可にする
						updateTypeAvailabilityForArea(false);
					} catch (error) {
						console.error('Failed to load cities:', error);
					}
				}

				// マップが準備できていることを確認してからフィルタを実行
				if (currentMap) {
					await updateMapWithFilter(prefecture, city);
					document.querySelector('.js-map-wrap')?.classList.remove('di_none');
				} else {
					// マップがまだ準備できていない場合は、少し待ってから実行
					setTimeout(async () => {
						if (currentMap) {
							await updateMapWithFilter(prefecture, city);
							document.querySelector('.js-map-wrap')?.classList.remove('di_none');
						}
					}, 1000);
				}
			}
		}
	};

	// SNSシェア
	const setSnsShare = () => {
		const shareButtons = document.querySelectorAll('.js-map-share');
		if (shareButtons.length) {
			var pageUrl = encodeURIComponent(document.URL);
			// console.log('pageUrl',pageUrl)
			var shareUrl = "";
			var sharePagetitle = encodeURIComponent(document.title);
			shareButtons.forEach(elem => {
				if (elem.querySelector('.js-shareX')) {
					// X
					shareUrl = 'https://twitter.com/share?url=' + pageUrl + '&text=' + sharePagetitle;
					elem.querySelector('.js-shareX').setAttribute('href', shareUrl);
				}
				if (elem.querySelector('.js-shareLine')) {
					// LINE
					shareUrl = 'https://social-plugins.line.me/lineit/share?url=' + pageUrl;
					elem.querySelector('.js-shareLine').setAttribute('href', shareUrl);
				}
			});
		}
	}


	// ============ ↑↑ FUNCTIONS ↑↑ =＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
	setParamForMap();
	setMap();
	searchLocation();
	setupPrefectureSelect();
	setupModeButtons();
	setupClearButton();
	setupSearchButton();
	setupMachineTypeFilter();
	sortProductFilterOptions();
	initializeSearchMode();
	initializeCheckboxStates();
	initializeFilterStates();
	// 復元した種類フィルタに応じて、都道府県／市区町村の選択可否を反映（既存の選択はリセットしない）
	updateFilterAvailability(false);
	// 復元した都道府県／市区町村に応じて、種類の選択可否を反映
	updateTypeAvailabilityForArea(false);
	// 復元した選択状態に応じて、選択解除ボタンの活性/非活性を反映
	updateClearButtonState();
	setSnsShare();

	
})(window.FUNCTIONS);
